import type {
  AdminCompanyDTO,
  AdminCreateCompanyDTO,
} from '@shared/schemas';
import { adminCompanySchema } from '@shared/schemas';
import type {
  CompanyCreateMemberDTO,
  CompanyManageableRole,
  CompanyMemberCreatedDTO,
  CompanyMemberInvitationResponseDTO,
  CompanyMembersQueryDTO,
  PaginatedCompanyMembersDTO,
} from '@shared/schemas/organization';
import {
  companyMemberCreatedSchema,
  companyMemberInvitationResponseSchema,
  paginatedCompanyMembersSchema,
} from '@shared/schemas/organization';
import type {
  AdminCompaniesQueryDTO,
  AdminCompaniesWithQuotaDTO,
  AdminCompanyQuotaDetailDTO,
  AdminCompanyQuotaExtraDTO,
  AdminCompanyQuotaUpdateDTO,
  PaginatedCompanyQuotaEventsDTO,
} from '@shared/schemas/quota';
import {
  adminCompaniesWithQuotaSchema,
  adminCompanyQuotaDetailSchema,
  paginatedCompanyQuotaEventsSchema,
} from '@shared/schemas/quota';
import { apiBaseUrl } from '@/lib/auth-client';
import { ApiError } from '@/lib/api-error';

function createUrl(path: string, query?: Record<string, string | number | undefined>) {
  const url = new URL(path, apiBaseUrl);
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== '') url.searchParams.set(key, String(value));
    });
  }
  return url;
}

async function parseResponse<T>(response: Response): Promise<T> {
  const payload = await response.json().catch(() => null) as ({
    error?: { code?: string; details?: Record<string, unknown>; message?: string };
  } & T) | null;
  if (!response.ok || payload === null) {
    throw new ApiError({
      code: payload?.error?.code,
      details: payload?.error?.details,
      message: payload?.error?.message ?? response.statusText ?? 'La requête a échoué.',
      status: response.status,
    });
  }
  return payload;
}

async function request<T>(
  method: 'GET' | 'PATCH' | 'POST' | 'PUT',
  path: string,
  options: {
    body?: Record<string, unknown>;
    query?: Record<string, string | number | undefined>;
  } = {},
) {
  const response = await fetch(createUrl(path, options.query), {
    body: options.body ? JSON.stringify(options.body) : undefined,
    credentials: 'include',
    headers: options.body ? { 'content-type': 'application/json' } : undefined,
    method,
  });
  return parseResponse<T>(response);
}

export type CompanyMembersScope =
  | { readonly type: 'active' }
  | { readonly companyId: string; readonly type: 'targeted' };

export const activeCompanyMembersScope = { type: 'active' } as const satisfies CompanyMembersScope;

function companyMembersPath(scope: CompanyMembersScope) {
  return scope.type === 'targeted'
    ? `/api/admin/companies/${scope.companyId}/members`
    : '/api/company/members';
}

export function listCompanyMembers(
  query: CompanyMembersQueryDTO,
  scope: CompanyMembersScope = activeCompanyMembersScope,
) {
  return request<PaginatedCompanyMembersDTO>('GET', companyMembersPath(scope), { query })
    .then((payload) => paginatedCompanyMembersSchema.parse(payload));
}

export function createCompanyMember(
  input: CompanyCreateMemberDTO,
  scope: CompanyMembersScope = activeCompanyMembersScope,
) {
  return request<CompanyMemberCreatedDTO>('POST', companyMembersPath(scope), {
    body: input,
  }).then((payload) => companyMemberCreatedSchema.parse(payload));
}

export function changeCompanyMemberRole(
  userId: string,
  role: CompanyManageableRole,
  scope: CompanyMembersScope = activeCompanyMembersScope,
) {
  return request<unknown>('PATCH', `${companyMembersPath(scope)}/${userId}/role`, { body: { role } });
}

export function setCompanyMemberActive(
  userId: string,
  active: boolean,
  scope: CompanyMembersScope = activeCompanyMembersScope,
) {
  return request<unknown>(
    'POST',
    `${companyMembersPath(scope)}/${userId}/${active ? 'reactivate' : 'deactivate'}`,
    active ? {} : { body: { reason: 'Compte désactivé depuis la console entreprise.' } },
  );
}

export function resendCompanyMemberInvitation(
  userId: string,
  scope: CompanyMembersScope = activeCompanyMembersScope,
): Promise<CompanyMemberInvitationResponseDTO> {
  return request<CompanyMemberInvitationResponseDTO>(
    'POST',
    `${companyMembersPath(scope)}/${userId}/invitation`,
  ).then((payload) => companyMemberInvitationResponseSchema.parse(payload));
}

export function listAdminCompanies(query: AdminCompaniesQueryDTO = {}): Promise<AdminCompaniesWithQuotaDTO> {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      params.set(key, String(value));
    }
  });
  const suffix = params.size > 0 ? `?${params.toString()}` : '';
  return request<unknown>('GET', `/api/admin/companies${suffix}`)
    .then((payload) => adminCompaniesWithQuotaSchema.parse(payload));
}

export function createAdminCompany(input: AdminCreateCompanyDTO): Promise<AdminCompanyDTO> {
  return request<{ data: AdminCompanyDTO }>('POST', '/api/admin/companies', {
    body: input,
  }).then((payload) => adminCompanySchema.parse(payload.data));
}

export function updateAdminCompanyQuota(
  companyId: string,
  input: AdminCompanyQuotaUpdateDTO,
): Promise<AdminCompanyQuotaDetailDTO> {
  return request<unknown>('PUT', `/api/admin/companies/${companyId}/quota`, { body: input })
    .then((payload) => adminCompanyQuotaDetailSchema.parse(payload));
}

export function addAdminCompanyQuotaExtra(
  companyId: string,
  input: AdminCompanyQuotaExtraDTO,
): Promise<AdminCompanyQuotaDetailDTO> {
  return request<unknown>('POST', `/api/admin/companies/${companyId}/quota/extra`, { body: input })
    .then((payload) => adminCompanyQuotaDetailSchema.parse(payload));
}

export function recomputeAdminCompanyQuota(companyId: string): Promise<AdminCompanyQuotaDetailDTO> {
  return request<unknown>('POST', `/api/admin/companies/${companyId}/quota/recompute`)
    .then((payload) => adminCompanyQuotaDetailSchema.parse(payload));
}

export function listAdminCompanyQuotaEvents(
  companyId: string,
  page: number,
  pageSize = 20,
): Promise<PaginatedCompanyQuotaEventsDTO> {
  return request<unknown>('GET', `/api/admin/companies/${companyId}/quota/events`, {
    query: { page, pageSize },
  }).then((payload) => paginatedCompanyQuotaEventsSchema.parse(payload));
}
